<script setup lang="ts">
import { storeToRefs } from 'pinia'
import '~/global/extensions/date' // Reflete em todas as datas (new Date) do sistema
import '~/global/extensions/string'
import { watch } from 'vue'
import { BaseToastHost } from '@cplug/design-system/components'
import { useAuthObserver } from '~/domain/shared/authentication/composables/useAuthObserver'
import { useThemePreferenceStore } from '~/domain/dashboard/settings/erp/shared'
import { useBrandStore } from '~/domain/shared/branding/stores/useBrandStore'
import { useBrandTheme } from '~/domain/shared/branding/composables/useBrandTheme'
import { useNoveltyBadgeRouteWatcher } from '~/domain/shared/navigation/composables/useNoveltyBadgeRouteWatcher'
import { useOnlineStoreFeatureAccess } from '~/domain/shared/navigation/composables/useOnlineStoreFeatureAccess'
import { useSupportChat } from '~/domain/shared/support/composables/useSupportChat'
import FloatingChatActions from '~/domain/shared/support/components/FloatingChatActions.vue'

const themePreferenceStore = useThemePreferenceStore()
const { isDark } = storeToRefs(themePreferenceStore)

const { brandName } = useBrandTheme()
const brandStore = useBrandStore()
const { current, isBagy, isTray } = storeToRefs(brandStore)

useHead({
  title: 'ERP',
  titleTemplate: () => `%s | ${brandName.value}`,
  bodyAttrs: {
    class: ['sizes-default typography-default']
  },
  htmlAttrs: {
    'data-brand': () => current.value
  },
  link: [
    {
      rel: 'icon',
      type: 'image/svg+xml',
      href: (): string => {
        if (isBagy.value) {
          return '/bagy-favicon.svg'
        }
        if (isTray.value) {
          return '/tray-favicon.svg'
        }
        return '/favicon.svg'
      }
    }
  ]
})

watch(isDark, (newTheme: boolean) => {
  document.documentElement.classList.toggle('dark', newTheme)
}, { immediate: true })

useAuthObserver()
useNoveltyBadgeRouteWatcher()
useSupportChat()
useOnlineStoreFeatureAccess()
</script>

<template>
  <div class="min-h-dvh bg-background text-foreground">
    <NuxtLoadingIndicator />
    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>
    <BaseToastHost />

    <div id="modals" />
    <div id="dropdowns" />
    <div id="popovers" />

    <FloatingChatActions />
  </div>
</template>

<style lang="scss">
* {
  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--muted-foreground);
    border-radius: var(--radius-md, 8px);
    border: 1px solid var(--border);
  }

  &::-webkit-scrollbar-track {
    background: var(--border);
  }

  &::-webkit-scrollbar-thumb:hover {
    background: var(--accent-foreground);
  }
}

.page-enter-active,
.page-leave-active {
  transition: all 0.3s;
}

.page-enter-from,
.page-leave-to {
  opacity: 0;
  filter: blur(1rem);
}
</style>
